import React, { useEffect, useState, useRef, useCallback } from "react";
import { leaveRequest, approveLeave, listOfAffectedCount } from "../../../../Api/apiUtils";
import {
  Page,
  PageHeader,
  Card,
  Button,
  Field,
  SearchInput,
  Toolbar,
  Table,
  Badge,
  StatusBadge,
  Loader,
  EmptyState,
  Callout,
  Dialog,
  useConfirm,
} from "../../../../ui";
import Pager from "./Pager";
import "./teacherForcast.scss";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { formatDate } from "../../../../utils/formatDate";
import { trackEvent, MixpanelEvents } from "../../../../utils/mixpanelTracking";

interface AffectedStudent {
  name: string;
  studentMobileNumber: string;
  courseName: string;
  classDateTimes  : number[];
  startTime: string;
  classesAffected: number[];
  studentTimezone: string;
  classmigratedList:any;

membershipType:any
}

/** Backend leave status name → the StatusBadge key that gives it the shared status colour. */
const LEAVE_STATUS_KEYS: Record<string, string> = {
  LEAVE_APPROVAL_PENDING: "pending",
  LEAVE_APPROVED: "approved",
  LEAVE_REJECTED: "rejected",
  LEAVE_WITHDRAWAL: "cancelled",
};
const leaveStatusKey = (name?: string | null) => (name ? LEAVE_STATUS_KEYS[name] ?? name : "");

const teacherNameOf = (record: any) =>
  [record?.teacherDetails?.userDetails?.firstName, record?.teacherDetails?.userDetails?.lastName].filter(Boolean).join(" ") ||
  "This teacher";

const TeacherForecastWizard = () => {
  const { confirm, dialog } = useConfirm();
  const [leaveData, setLeaveData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [selectedLeave, setSelectedLeave] = useState<any>(null);
  const [pagination, setPagination] = useState({
    pageSize: 10,
    totalElements: 0,
    totalPages: 0,
  });
  const [localLoading, setLocalLoading] = useState<{ [key: string]: boolean }>({}); // Track loading state for each button
  const [searchQuery, setSearchQuery] = useState<string>(""); // State for search query
  const [affectedCounts, setAffectedCounts] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [affectedStudents, setAffectedStudents] = useState<AffectedStudent[]>([]);
  const fetchLeaveData = useCallback(async (page: number, limit: number = 15, search?: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await leaveRequest(page, limit, search);
      console.log("Leave API Response:", response);

      const newData = Array.isArray(response) ? response : response.content || [];
      setLeaveData(newData);
      
      // Update pagination info from API response
      setPagination({
        pageSize: response.size || limit,
        totalElements: response.totalElements || 0,
        totalPages: response.totalPages || 0,
      });
    } catch (err) {
      console.error("Error fetching leave data:", err);
      setError("Failed to fetch leave data.");
      toast.error("An error occurred while fetching teacher leave history.");
    } finally {
      setLoading(false);
    }
  }, []);

  const isFirstRender = useRef(true);

  // Initial load - fetch data immediately on mount
  useEffect(() => {
    fetchLeaveData(0, pagination.pageSize, "");
  }, []);

  // Debounce search query and fetch data (skip first render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const timer = setTimeout(() => {
      // Reset to first page when search changes
      setCurrentPage(0);
      fetchLeaveData(0, pagination.pageSize, searchQuery);
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timer);
  }, [searchQuery, pagination.pageSize, fetchLeaveData]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < pagination.totalPages) {
      setCurrentPage(newPage);
      fetchLeaveData(newPage, pagination.pageSize, searchQuery);
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: number[] = [];
    const totalPages = pagination.totalPages;
    const current = currentPage;
    
    // Show max 5 page numbers
    let startPage = Math.max(0, current - 2);
    let endPage = Math.min(totalPages - 1, current + 2);
    
    // Adjust if we're near the beginning or end
    if (current < 2) {
      endPage = Math.min(4, totalPages - 1);
    }
    if (current > totalPages - 3) {
      startPage = Math.max(0, totalPages - 5);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const handleApprove = async (record: any, status: any) => {
    setLocalLoading((prev) => ({ ...prev, [record.id]: true })); // Set local loading for this record
    try {
      const response = await approveLeave(record.id, status);
      console.log("Approve API Response:", response);

      if (response && response === "Succesful") {
        trackEvent(MixpanelEvents.TEACHER_LEAVE, {
          success: true,
          teacherId: record.teacherDetails?.id,
          teacherName: record.teacherDetails?.userDetails?.firstName + " " + record.teacherDetails?.userDetails?.lastName,
          leaveId: record.id,
          status: status,
        });
        
        toast.success("Status Updated Successfully");
        await fetchLeaveData(currentPage, pagination.pageSize, searchQuery); // Refresh UI immediately
      } else {
        toast.error("Error While Updating Status");
      }
    } catch (error: any) {
      console.error("Error approving teacher leave:", error);
      toast.error(error.response.data.message);
      trackEvent(MixpanelEvents.TEACHER_LEAVE, {
        teacherId: record.teacherDetails?.id,
        teacherName: record.teacherDetails?.userDetails?.firstName + " " + record.teacherDetails?.userDetails?.lastName,
        leaveId: record.id,
        status: status,
        error: error.response.data.message,
      });
     
    } finally {
      setLocalLoading((prev) => ({ ...prev, [record.id]: false })); // Reset local loading for this record
    }
  };

  const handleShowAffected = async (leave: any) => {
    setSelectedLeave(leave); // Set the selected leave for modal status check
    const payload = {
      teacherId: leave.teacherDetails?.id,
      fromDate: leave.fromDate?.split(' ')[0],
      toDate: leave.toDate?.split(' ')[0],
      fromTime: leave.startTime,
      toTime: leave.endTime,
    };
    try {
      const response = await listOfAffectedCount(payload);
      setAffectedStudents(Array.isArray(response.data.affectedstudentList) ? response.data.affectedstudentList : []);
      setShowModal(true);
    } catch (error) {
      setAffectedStudents([]); // fallback to empty array
      toast.error("Failed to fetch affected students");
    }
  };

  // Server handles filtering, use leaveData directly
  const filteredLeaveData = leaveData;


  const confirmAndApprove = async (record: any, status: "LEAVE_REJECTED" | "LEAVE_WITHDRAWAL") => {
    const teacherName = teacherNameOf(record);
    const ok = await confirm(
      status === "LEAVE_REJECTED"
        ? {
            title: "Reject leave?",
            message: `${teacherName}'s leave request will be rejected.`,
            confirmLabel: "Reject leave",
            tone: "danger",
          }
        : {
            title: "Withdraw approved leave?",
            message: `${teacherName}'s approved leave will be withdrawn.`,
            confirmLabel: "Withdraw leave",
            tone: "danger",
          },
    );
    if (!ok) return;
    handleApprove(record, status);
  };

  const rangeStart = currentPage * pagination.pageSize + 1;
  const rangeEnd = Math.min((currentPage + 1) * pagination.pageSize, pagination.totalElements);

  return (
    <Page>
      <PageHeader
        eyebrow="Teacher Operations"
        title="Leave Request"
        subtitle="Approve, reject or withdraw teacher leave, and see which classes it affects."
      />

      <Card>
        <Toolbar>
          <Field label="Search" className="ds-toolbar__grow">
            {(ctl) => (
              <SearchInput
                {...ctl}
                placeholder="Search by name or mobile number"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            )}
          </Field>
          {pagination.totalElements > 0 && (
            <p className="ds-toolbar__end ds-text-muted leave-range" aria-live="polite">
              Showing <span className="ds-num">{rangeStart}–{rangeEnd}</span> of{" "}
              <span className="ds-num">{pagination.totalElements}</span> requests
            </p>
          )}
        </Toolbar>
      </Card>

      {loading && <Loader label="Loading leave requests…" />}

      {error && (
        <Callout tone="danger" title="Couldn't load leave requests">
          {error}
        </Callout>
      )}

      {!loading && !error && filteredLeaveData.length === 0 && (
        <EmptyState
          title="No leave requests"
          description={
            searchQuery
              ? "No leave requests match this search. Check the name or mobile number, or clear the search."
              : "Leave requests from teachers will show up here."
          }
        />
      )}

      {!loading && !error && filteredLeaveData.length > 0 && (
        <Card flush>
          <Table caption="Teacher leave requests" stickyHeader wrapClassName="leave-table-wrap" className="leave-table">
            <thead>
              <tr>
                <th scope="col">Teacher</th>
                <th scope="col">Mobile number</th>
                <th scope="col">Slots affected</th>
                <th scope="col" className="ds-num">Leave deducted</th>
                <th scope="col">From</th>
                <th scope="col">To</th>
                <th scope="col">Start</th>
                <th scope="col">End</th>
                <th scope="col">Applied on</th>
                <th scope="col">Classes impacted</th>
                <th scope="col">Reason</th>
                <th scope="col">Status</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredLeaveData.map((leave, index) => (
                <tr key={index}>
                  <td>
                    <Link
                      className="ds-link"
                      to={`/pages/teacher-profile/${leave.teacherDetails.userDetails.credential?.id}/${leave.teacherDetails.id}`}
                    >
                      {leave.teacherDetails?.userDetails?.firstName || "N/A"} {leave.teacherDetails?.userDetails?.lastName || "N/A"}
                    </Link>
                  </td>
                  <td className="ds-num">{leave.teacherDetails?.userDetails?.credential?.mobileNumber || "N/A"}</td>
                  <td>{leave.leaveDuration || "N/A"}</td>
                  <td className="ds-num">{leave.leaveBalance || 0.0}</td>
                  <td className="leave-table__nowrap">{formatDate(leave.fromDate, "shortDate")}</td>
                  <td className="leave-table__nowrap">{formatDate(leave.toDate, "shortDate")}</td>
                  <td className="ds-num">{leave.startTime || "-"}</td>
                  <td className="ds-num">{leave.endTime || "-"}</td>
                  <td>
                    <div className="leave-table__applied">
                      <span className="leave-table__nowrap">{formatDate(leave.createdOn || leave.createdAt, "dateTime")}</span>
                      {(leave.isEmergencyLeave === true || leave.isEmergencyLeave === "true") && (
                        <Badge tone="accent">Last-hour emergency</Badge>
                      )}
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="leave-impact"
                      onClick={() => handleShowAffected(leave)}
                      aria-label={`Show students affected by ${teacherNameOf(leave)}'s leave`}
                    >
                      <span>
                        First four: <span className="ds-num ds-strong">{leave.totalFirstFourClassesAffected}</span>
                      </span>
                      <span>
                        Other: <span className="ds-num ds-strong">{leave.otherClassesAffected}</span>
                      </span>
                    </button>
                  </td>
                  <td className="leave-table__reason">{leave.reasons || "-"}</td>
                  <td>
                    {leave.leaveStatus?.type ? (
                      <StatusBadge status={leaveStatusKey(leave.leaveStatus?.name)} label={leave.leaveStatus.type} />
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    <div className="leave-actions">
                      {leave.leaveStatus?.name === "LEAVE_APPROVAL_PENDING" && (
                        <>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleApprove(leave, "LEAVE_APPROVED")}
                            loading={localLoading[leave.id]}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="leave-actions__danger"
                            onClick={() => confirmAndApprove(leave, "LEAVE_REJECTED")}
                            loading={localLoading[leave.id]}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {leave.leaveStatus?.name === "LEAVE_APPROVED" && leave.reasons !== "Due To NoShow" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="leave-actions__danger"
                          onClick={() => confirmAndApprove(leave, "LEAVE_WITHDRAWAL")}
                          loading={localLoading[leave.id]}
                        >
                          Withdraw
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {pagination.totalPages > 1 && (
        <Pager
          currentPage={currentPage}
          totalPages={pagination.totalPages}
          pageNumbers={getPageNumbers()}
          onChange={handlePageChange}
        />
      )}

      <Dialog size="xl" open={showModal} onClose={() => setShowModal(false)} title="Affected students">
        {selectedLeave?.leaveStatus?.name === "LEAVE_APPROVED" ? (
          <Callout tone="success" title="Leave already approved" />
        ) : affectedStudents.length === 0 ? (
          <EmptyState title="No affected students" description="No student classes fall inside this leave." />
        ) : (
          <Table caption="Students affected by this leave">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Mobile</th>
                <th scope="col">Course</th>
                <th scope="col">Slot date and time</th>
                <th scope="col">Classes affected</th>
                <th scope="col">Membership</th>
                <th scope="col">Timezone</th>
              </tr>
            </thead>
            <tbody>
              {affectedStudents.map((student, idx) => (
                <tr key={idx}>
                  <td>{student.name}</td>
                  <td className="ds-num">{student.studentMobileNumber}</td>
                  <td>{student.courseName}</td>
                  <td>
                    {student.classDateTimes?.map((date: any, i: any) => (
                      <div key={i} className="ds-num leave-table__nowrap">{date}</div>
                    ))}
                  </td>
                  <td>
                    {student.classesAffected.map((cls, i) => {
                      const migrated = student.classmigratedList?.[i]?.migrated === "Yes";
                      return (
                        <div key={i} className="leave-class">
                          Class <span className="ds-num">{cls}</span>
                          {migrated && <Badge tone="info">Migrated</Badge>}
                        </div>
                      );
                    })}
                  </td>
                  <td>{student?.membershipType ? student.membershipType : "-"}</td>
                  <td>{student.studentTimezone}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Dialog>
      {dialog}
    </Page>
  );
};

export default TeacherForecastWizard;